import { memo, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Webcam from "react-webcam";
import { loadModelsAndDetectFaces } from "./face-detection";

interface FaceAreaProps {
  startAnimation: boolean;
  animationTime: number;
  detectFace?: boolean;
  webcamRef: React.RefObject<Webcam>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  getFaceDetectionValues?: (
    isFaceInsideArea: boolean,
    isFaceApiReady: boolean
  ) => void;
}

const FaceArea = memo(function FaceArea(props: FaceAreaProps): JSX.Element {
  const [isFaceInsideArea, setIsFaceInsideArea] = useState(false);
  const ellipseRef = useRef<SVGEllipseElement>(null);
  const intervalIdRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (containerRef.current) {
      const updateDimensions = () => {
        const { width, height } = containerRef.current!.getBoundingClientRect();
        setDimensions({ width, height });
      };

      updateDimensions();
      window.addEventListener("resize", updateDimensions);
      return () => window.removeEventListener("resize", updateDimensions);
    }
  }, []);

  useEffect(() => {
    if (!props.detectFace) return;

    intervalIdRef.current = loadModelsAndDetectFaces(
      props.webcamRef,
      props.canvasRef,
      ellipseRef,
      (isFaceInsideArea, isFaceApiReady) => {
        setIsFaceInsideArea(isFaceInsideArea);
        props.getFaceDetectionValues?.(isFaceInsideArea, isFaceApiReady);
      }
    ) as number;

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [props.canvasRef, props.webcamRef, props.detectFace, props.getFaceDetectionValues]);

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <svg
        viewBox="0 0 100 50"
        preserveAspectRatio="none"
        className="absolute h-full w-full rounded-lg"
        width={dimensions.width}
        height={dimensions.height}
      >
        <defs>
          <mask id="mask" x="0" y="0" width="100%" height="100%">
            <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
            <ellipse
              ref={ellipseRef}
              cx="50"
              cy="25"
              rx="18"
              ry="20"
              fill="#000"
            />
          </mask>
        </defs>

        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="#000"
          mask="url(#mask)"
          fillOpacity="0.7"
        />

        <AnimatePresence>
          {(!props.detectFace || isFaceInsideArea) && (
            <motion.ellipse
              cx="50"
              cy="25"
              rx="18"
              ry="20"
              fill="transparent"
              initial={{ strokeWidth: 0 }}
              animate={{ strokeWidth: 1 }}
              exit={{ strokeWidth: 0 }}
              stroke="#DA291C"
              strokeWidth="1"
            />
          )}
        </AnimatePresence>

        <motion.path
          d="M 50 5 A 18 20 0 0 0 50 45"
          stroke="white"
          strokeWidth="1.1"
          fill="transparent"
          initial={{ pathLength: 0, pathOffset: 1 }}
          animate={
            props.startAnimation
              ? {
                  pathLength: 2,
                  pathOffset: 0,
                  transition: { duration: props.animationTime },
                }
              : {}
          }
        />

        <motion.path
          d="M 50 5 A 18 20 0 0 1 50 45"
          stroke="white"
          strokeWidth="1.1"
          fill="transparent"
          initial={{ pathLength: 0, pathOffset: 1 }}
          animate={
            props.startAnimation
              ? {
                  pathLength: 2,
                  pathOffset: 0,
                  transition: { duration: props.animationTime },
                }
              : {}
          }
        />
      </svg>
    </div>
  );
});

export default FaceArea;
